import { useQueryClient } from '@tanstack/vue-query';
import { DateTime } from 'luxon';

import { Event } from '@/shared/interfaces/event';
import { Feed } from '@/shared/interfaces/feed';
import { FeedTypeEnum } from '@/enums/values.enum';
import { Timetable } from '@/shared/interfaces/timeTables';
import { useAuthStore } from '@/stores/auth/authStore';
import { ValuesEnum } from '@/enums/values.enum';
import useCacheService from '@/shared/composables/useCacheService';
import useEvents from '@/modules/events/composables/useEvents';
import useFeed from '@/modules/dashboard/composables/useFeed';
import usePosts from '@/modules/news/composables/usePosts';
import useStorageService from '@/shared/composables/useStorageService';
import useTimeTables from '@/modules/timetables/composables/useTimeTables';
import useMaps from '@/modules/maps/composables/useMaps';
import { ApiMap } from '@/shared/interfaces/map';
import useProfileUtils from '@/modules/profile/composables/useProfileUtils';

// Init
const { get, set } = useStorageService();
const { getTimestamps } = useCacheService();

const usePreload = () => {
	// Init
	const queryClient = useQueryClient();
	const { getFeed } = useFeed(false, false);
	const { getPostDetail } = usePosts();
	const { getEventApiCall, getEventParticipants, getEventTicketsApiCall, getEventsApiCall } = useEvents();
	const { getTimeTableApiCall, getTimetablesApiCall } = useTimeTables();
	const { getMapApiCall, getMapsApiCall } = useMaps();
	const authStore = useAuthStore();
	const { getLoginTokenApiCall } = useProfileUtils();

	/**
	 * Calculate size of data in KB
	 */
	const calculateDataSize = (data: any): number => {
		try {
			return new Blob([JSON.stringify(data)]).size / 1024; // Return in KB
		} catch {
			return 0;
		}
	};

	/**
	 * Check if data should be cached based on size
	 * Skip caching for very large items to prevent quota errors
	 */
	const shouldCacheData = (data: any, queryKey: any): boolean => {
		const sizeKB = calculateDataSize(data);
		const MAX_CACHE_SIZE_KB = 500; // Don't cache items larger than 500KB

		if (sizeKB > MAX_CACHE_SIZE_KB) {
			console.warn(`[Preload] Skipping cache for ${queryKey.join('-')} - too large (${sizeKB.toFixed(2)}KB > ${MAX_CACHE_SIZE_KB}KB)`);
			return false;
		}
		return true;
	};

	// prefetch
	const prefetch = async (queryKey: any, queryFn: any, isInfinite?: boolean) => {
		if (!isInfinite) {
			await queryClient.prefetchQuery({
				queryKey,
				queryFn: async () => await queryFn,
				cacheTime: 1000 * 60 * 60 * 24 * 365,
			});
		} else {
			await queryClient.prefetchInfiniteQuery({
				queryKey,
				queryFn: async () => await queryFn,
				getNextPageParam: (lastPage: any) => {
					return lastPage.last_page >= lastPage.current_page + 1 ? lastPage.current_page + 1 : undefined;
				},
				cacheTime: 1000 * 60 * 60 * 24 * 365,
			});
		}

		// Save
		let keys = await get('cachedDataKeys');
		if (!keys) {
			keys = [queryKey.join('-')];
		} else {
			if (!keys.includes(queryKey.join('-'))) {
				keys.push(queryKey.join('-'));
			}
		}
		await set('cachedDataKeys', keys);

		const strKey = queryKey.join('-');

		// queryFn.then((data: any) => {
		// 	set(strKey, data);
		// })
		const queryCachedData = queryClient.getQueryCache().find(queryKey);
		const data = queryCachedData?.state.data;

		// Only cache if data size is reasonable
		if (data && shouldCacheData(data, queryKey)) {
			set(strKey, data);
		}
	};

	// Preload feed
	const preloadFeed = async () => {
		await prefetch(['feedDashboard'], getFeed(1), true);

		// Preload first 3 posts
		const feedDashboard = queryClient.getQueryCache().find(['feedDashboard']);
		if (feedDashboard?.state.data) {
			const feedList: any = feedDashboard?.state.data;
			await preloadPosts(
				feedList.pages[0]
					.filter((post: Feed) => post.type === FeedTypeEnum.post)
					.map((post: Feed) => post.data.id)
					.slice(0, 20),
			);
			await preloadEventsById(
				feedList.pages[0]
					.filter((post: Feed) => {
						return post.type === FeedTypeEnum.event || post.type === FeedTypeEnum.event_now || post.type === FeedTypeEnum.event_soon;
					})
					.map((post: Feed) => post.data.id)
					.slice(0, 20),
			);
		}
	};

	// Preload posts
	const preloadPosts = async (postsIdsToPreload: number[]) => {
		postsIdsToPreload.forEach(async (postId) => {
			await prefetch(['post', postId], getPostDetail(postId));
		});
	};

	// Preload posts
	const preloadEventsById = async (eventsIdsToPreload: number[]) => {
		eventsIdsToPreload.forEach(async (eventId) => {
			await prefetch(['event', eventId], getEventApiCall(eventId));
		});
	};

	// Preaload events
	const preloadEvents = async () => {
		if (authStore.isAuthenticated()) {
			await prefetch(['events', '', 'going'], getEventsApiCall('', 1, 'going'), true);

			const events = queryClient.getQueryCache().find(['events', '', 'going']);
			if (events?.state.data) {
				const eventsList: any = events?.state.data;
				await fetchEventDetails(eventsList.pages[0].data, false);
			}

			await prefetch(['events', '', 'all'], getEventsApiCall('', 1, 'all'), true);

			const eventsAll = queryClient.getQueryCache().find(['events', '', 'all']);
			if (eventsAll?.state.data) {
				const eventsListAll: any = eventsAll?.state.data;
				await fetchEventDetails(eventsListAll.pages[0].data, false);
			}
		}
	};

	// fetchEventDetails
	const fetchEventDetails = async (events: Event[], tickets = false) => {
		if (!events?.length) {
			return;
		}

		events.forEach((event: Event) => {
			setTimeout(async () => {
				await prefetch(['event', event.id], getEventApiCall(event.id));

				setTimeout(async () => {
					await prefetch(['eventParticipants', event.id], getEventParticipants(event.id));
				}, 200);

				if (tickets) {
					await prefetch(['eventTickets', event.id], getEventTicketsApiCall(event.id));
				}
			}, 500);
		});
	};

	// Preload tickets
	const preloadTickets = async () => {
		if (await authStore.isAuthenticated()) {
			await prefetch(['events', '', 'going'], getEventsApiCall('', 1, 'going'), true);
			const events = queryClient.getQueryCache().find(['events', '', 'going']);
			if (events?.state.data) {
				const eventsList: any = events?.state.data;
				await fetchEventDetails(eventsList.pages[0].data, true);
			}
		}
	};

	// Preload timetables
	const preloadTimetables = async () => {
		await prefetch(['timeTables'], getTimetablesApiCall());

		// Preload only first 3 timeTables to avoid quota issues
		// User can load others on demand
		const timeTables = queryClient.getQueryCache().find(['timeTables']);
		if (timeTables?.state.data) {
			const timeTablesList: any = timeTables?.state.data;
			const timetablesToPreload = timeTablesList.slice(0, 3); // Only preload first 3

			console.log(`[Preload] Preloading ${timetablesToPreload.length} of ${timeTablesList.length} timetables to conserve storage`);

			for (const timeTable of timetablesToPreload) {
				await prefetch(['timeTable', timeTable.id], getTimeTableApiCall(timeTable.id));
			}
		}
	};

	// Preload maps
	const preloadMaps = async () => {
		await prefetch(['maps'], getMapsApiCall());

		// Preload maps
		const maps = queryClient.getQueryCache().find(['maps']);
		if (maps?.state.data) {
			const mapsList: any = maps?.state.data;
			if (mapsList.length) {
				mapsList.forEach(async (map: ApiMap) => {
					await prefetch(['map', map.id], getMapApiCall(map.id));
				});
			}
		}
	};

	// Preload login token for guest topup
	const preloadLoginToken = async () => {
		await getLoginTokenApiCall();
	};

	// Preload cache
	const preloadCache = async () => {
		// Init
		const timestamps = await get(ValuesEnum.StoreCacheTimestamps);
		const timestampsAuth = await get(ValuesEnum.StoreAuthCacheTimestamps);

		try {
			const cacheTimestamps = await getTimestamps();

			// If user is authenticated
			if (authStore.isAuthenticated()) {
				await set(ValuesEnum.StoreAuthCacheTimestamps, true);

				// Preload login token for guest topup
				await preloadLoginToken();
			}

			if (!timestamps || !timestampsAuth) {
				await preloadAll();
			} else {
				if (DateTime.fromISO(timestamps.feed) < DateTime.fromISO(cacheTimestamps.feed)) {
					await preloadFeed();
				}

				if (DateTime.fromISO(timestamps.events) < DateTime.fromISO(cacheTimestamps.events)) {
					await preloadEvents();
				}

				if (DateTime.fromISO(timestamps.tickets) < DateTime.fromISO(cacheTimestamps.tickets)) {
					await preloadTickets();
				}

				// if (DateTime.fromISO(timestamps.bookings) < DateTime.fromISO(cacheTimestamps.bookings)) {
				//   await this.preloadBookings();
				// }

				// if (DateTime.fromISO(timestamps.booking_items) < DateTime.fromISO(cacheTimestamps.booking_items)) {
				//   await this.preloadBookingItems();
				// }

				if (DateTime.fromISO(timestamps.timetables) < DateTime.fromISO(cacheTimestamps.timetables)) {
					await preloadTimetables();
				}

				if (DateTime.fromISO(timestamps.maps) < DateTime.fromISO(cacheTimestamps.maps)) {
					await preloadMaps();
				}
			}

			// Save caches timestamps
			await set(ValuesEnum.StoreCacheTimestamps, cacheTimestamps);
		} catch (e: any) {
			console.log(e);
		}
	};

	const preloadAll = async () => {
		await preloadFeed();
		await new Promise((resolve) => setTimeout(resolve, 300));
		await preloadEvents();
		await new Promise((resolve) => setTimeout(resolve, 300));
		await preloadTickets();
		await new Promise((resolve) => setTimeout(resolve, 300));
		await preloadTimetables();
		await new Promise((resolve) => setTimeout(resolve, 300));
		await preloadMaps();
		// await this.preloadBookings();
		// await this.preloadBookingItems();
	};

	return {
		preloadCache,
	};
};

export default usePreload;
